/**
 * Hook to load contacts for a student, with loading/error states.
 * Exposes delete and setPrimary mutations with optimistic updates.
 */

import { useState, useEffect, useCallback } from 'react';
import {
  getContactsForStudent,
  getStudent,
  getEntity,
  fetchContacts,
  deleteContact as apiDeleteContact,
  setPrimaryContact as apiSetPrimaryContact,
  type Contact,
  type Student,
  type Entity,
} from '@/lib/data';

const isMockData = import.meta.env.VITE_MOCK_DATA === 'true';

interface ContactsData {
  contacts: Contact[];
  student: Student | undefined;
  entity: Entity | undefined;
  loading: boolean;
  error: string | null;
  refetch: () => void;
  deleteContact: (id: string) => Promise<void>;
  setPrimaryContact: (id: string) => Promise<void>;
}

export function useContacts(
  studentId: string,
  entityId: string | undefined,
): ContactsData {
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const student = getStudent(studentId);
  const entity = entityId ? getEntity(entityId) : undefined;

  const loadContacts = useCallback(async () => {
    if (!studentId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      if (isMockData) {
        setContacts(getContactsForStudent(studentId));
      } else {
        const result = await fetchContacts(studentId);
        setContacts(result);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load contacts');
    } finally {
      setLoading(false);
    }
  }, [studentId]);

  useEffect(() => {
    loadContacts();
  }, [loadContacts]);

  const deleteContact = useCallback(async (id: string) => {
    const prev = contacts;
    // Optimistic removal
    setContacts((c) => c.filter((item) => item.id !== id));
    try {
      if (!isMockData) {
        await apiDeleteContact(id);
      }
    } catch (err) {
      // Rollback on failure
      setContacts(prev);
      throw err;
    }
  }, [contacts]);

  const setPrimaryContact = useCallback(async (id: string) => {
    const target = contacts.find((c) => c.id === id);
    if (!target) return;

    const prev = contacts;
    // Optimistic: set target to rank 1, bump others of same typeId that were ranked above
    setContacts((current) =>
      current.map((c) => {
        if (c.id === id) return { ...c, rank: 1 };
        if (c.typeId === target.typeId && c.rank < target.rank) return { ...c, rank: c.rank + 1 };
        return c;
      }),
    );
    try {
      if (!isMockData) {
        await apiSetPrimaryContact(id);
        // Refetch to get server-authoritative ranks
        await loadContacts();
      }
    } catch (err) {
      setContacts(prev);
      throw err;
    }
  }, [contacts, loadContacts]);

  return { contacts, student, entity, loading, error, refetch: loadContacts, deleteContact, setPrimaryContact };
}
